import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/api";
import { useAuth, can } from "@/auth";
import { useI18n } from "@/i18n";
import { ChatCircleDots, ClockCounterClockwise, Trash, PaperPlaneTilt, X } from "@phosphor-icons/react";

/* ---------------------------------------------------------------------------
   <CommentsThread />
   Unified comments + audit log feed for a single entity (opportunity / plan).
   Props: entityType ('opportunity' | 'account_plan'), entityId (string),
          onClose (optional — when used as drawer)
   --------------------------------------------------------------------------- */
export function CommentsThread({ entityType, entityId, onClose, embedded = false }) {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const canPost = can(user, "sales");
  const [rows, setRows] = useState(null);
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);

  const load = () =>
    api.get(`/comments/${entityType}/${encodeURIComponent(entityId)}`).then((r) => setRows(r.data.rows));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [entityType, entityId]);

  const send = async () => {
    const body = text.trim();
    if (!body) return;
    setPosting(true);
    try {
      await api.post(`/comments/${entityType}/${encodeURIComponent(entityId)}`, { text: body });
      setText("");
      await load();
    } finally { setPosting(false); }
  };
  const remove = async (cid) => {
    if (!confirm(t("comments.confirmDelete"))) return;
    await api.delete(`/comments/${cid}`);
    await load();
  };

  return (
    <div className={embedded ? "" : "fixed inset-y-0 right-0 w-full md:w-[440px] z-50 shadow-2xl"}
         style={!embedded ? { background: "var(--bg-elevated)", borderLeft: "1px solid var(--border)" } : {}}
         data-testid="comments-thread">
      <div className={embedded ? "" : "h-full flex flex-col"}>
        {!embedded && (
          <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: "1px solid var(--border)" }}>
            <div className="flex items-center gap-2">
              <ChatCircleDots size={18} style={{ color: "var(--accent)" }} weight="fill" />
              <div>
                <div className="text-[15px] font-semibold tracking-tight">{t("comments.title")}</div>
                <div className="text-[11px] font-mono" style={{ color: "var(--text-tertiary)" }}>{entityId}</div>
              </div>
            </div>
            <button onClick={onClose} className="p-2 rounded-full hover:bg-[var(--bg-subtle)]" data-testid="comments-close">
              <X size={16} />
            </button>
          </div>
        )}

        <div className={embedded ? "space-y-3" : "flex-1 overflow-y-auto px-5 py-4 space-y-3"}>
          {!rows ? (
            <div className="text-[12px] font-mono" style={{ color: "var(--text-tertiary)" }}>{t("common.loading")}</div>
          ) : rows.length === 0 ? (
            <div className="text-[13px] text-center py-8" style={{ color: "var(--text-tertiary)" }}>{t("comments.empty")}</div>
          ) : rows.map((r) => (
            <ActivityCard key={r.id} row={r} onDelete={remove} user={user} lang={lang} t={t} />
          ))}
        </div>

        {canPost && (
          <div className={embedded ? "mt-4 pt-4" : "px-5 py-4"} style={{ borderTop: embedded ? "1px solid var(--border)" : "1px solid var(--border)" }}>
            <div className="flex gap-2">
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={2}
                placeholder={t("comments.placeholder")}
                className="input-tech"
                style={{ resize: "none" }}
                data-testid="comments-input"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send();
                }}
              />
              <button
                onClick={send}
                disabled={posting || !text.trim()}
                className="btn-accent flex items-center gap-1 shrink-0"
                data-testid="comments-send"
                title="Ctrl/⌘ + Enter"
              >
                <PaperPlaneTilt size={14} weight="fill" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ActivityCard({ row, onDelete, user, t, lang }) {
  const isComment = row.kind === "comment";
  const isMine = row.user_email === user?.email;
  const canDelete = isComment && (isMine || user?.role === "admin");
  const time = new Date(row.created_at).toLocaleString();
  const initials = (row.user_name || row.user_email || "?").trim().slice(0, 2).toUpperCase();
  const [translated, setTranslated] = useState(null); // {text, lang}
  const [translating, setTranslating] = useState(false);

  const translate = async () => {
    if (!row.text) return;
    setTranslating(true);
    try {
      const r = await api.post("/translate", { texts: [row.text], target_lang: lang });
      const t0 = r.data.translations?.[0];
      if (t0 && t0 !== row.text) setTranslated({ text: t0, lang });
      else setTranslated({ text: row.text, lang, sameAsOriginal: true });
    } catch (e) {
      toast.error(lang === "fr" ? "Échec de la traduction" : "Translation failed");
    } finally {
      setTranslating(false);
    }
  };

  const KIND_META = {
    comment: { color: "var(--text-primary)", icon: ChatCircleDots, label: t("comments.kind.comment") },
    create:  { color: "var(--success)", icon: ClockCounterClockwise, label: t("comments.kind.create") },
    update:  { color: "var(--info)", icon: ClockCounterClockwise, label: t("comments.kind.update") },
    delete:  { color: "var(--accent)", icon: ClockCounterClockwise, label: t("comments.kind.delete") },
  };
  const meta = KIND_META[row.kind] || KIND_META.comment;
  const Icon = meta.icon;

  return (
    <div className="p-3 rounded-[10px]" style={{ background: isComment ? "var(--bg-elevated)" : "var(--bg-subtle)", border: "1px solid var(--border)" }}
         data-testid={`comment-${row.id}`}>
      <div className="flex items-start gap-2.5">
        {isComment ? (
          <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-semibold text-white shrink-0"
               style={{ background: "var(--accent)" }}>
            {initials}
          </div>
        ) : (
          <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0" style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)" }}>
            <Icon size={12} style={{ color: meta.color }} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <div className="text-[12.5px] font-semibold truncate" style={{ color: "var(--text-primary)" }}>
              {row.user_name || row.user_email}
              {!isComment && <span className="ml-2 text-[10px] uppercase tracking-wider font-medium" style={{ color: meta.color }}>{meta.label}</span>}
            </div>
            <div className="text-[10.5px] font-mono shrink-0" style={{ color: "var(--text-tertiary)" }}>{time}</div>
          </div>
          <div className="text-[13px] mt-0.5" style={{ color: "var(--text-primary)", whiteSpace: "pre-wrap" }}>{row.text}</div>
          {isComment && row.text && row.text.length > 6 && (
            <div className="mt-1">
              {translated ? (
                <>
                  {!translated.sameAsOriginal && (
                    <div className="mt-2 p-2 rounded-[8px] text-[13px]"
                         style={{ background: "var(--bg-subtle)", border: "1px dashed var(--border)", whiteSpace: "pre-wrap" }}
                         data-testid={`comment-translated-${row.id}`}>
                      <div className="text-[10px] font-mono uppercase tracking-wide mb-1" style={{ color: "var(--text-tertiary)" }}>
                        {lang === "fr" ? "Traduction · FR" : lang === "tr" ? "Çeviri · TR" : "Translation · EN"}
                      </div>
                      {translated.text}
                    </div>
                  )}
                  <button onClick={() => setTranslated(null)}
                          className="text-[10.5px] mt-1 hover:underline" data-testid={`comment-untranslate-${row.id}`}
                          style={{ color: "var(--text-tertiary)" }}>
                    {translated.sameAsOriginal
                      ? (lang === "fr" ? "✓ Déjà dans la bonne langue" : lang === "tr" ? "✓ Zaten doğru dilde" : "✓ Already in the target language")
                      : (lang === "fr" ? "Masquer la traduction" : lang === "tr" ? "Çeviriyi gizle" : "Hide translation")}
                  </button>
                </>
              ) : (
                <button onClick={translate} disabled={translating}
                        className="text-[10.5px] hover:underline inline-flex items-center gap-1"
                        data-testid={`comment-translate-${row.id}`}
                        style={{ color: "var(--text-tertiary)" }}>
                  {translating
                    ? (lang === "fr" ? "Traduction…" : lang === "tr" ? "Çeviriliyor…" : "Translating…")
                    : (lang === "fr" ? `🌐 Traduire en FR` : lang === "tr" ? `🌐 TR'ye çevir` : `🌐 Translate to EN`)}
                </button>
              )}
            </div>
          )}
          {row.diff && Object.keys(row.diff).length > 0 && (
            <div className="mt-2 space-y-0.5">
              {Object.entries(row.diff).slice(0, 6).map(([k, [oldV, newV]]) => (
                <div key={k} className="text-[11px] font-mono" style={{ color: "var(--text-secondary)" }}>
                  <span style={{ color: "var(--text-tertiary)" }}>{k}:</span>{" "}
                  <span style={{ textDecoration: "line-through" }}>{formatVal(oldV)}</span>
                  <span style={{ color: "var(--accent)" }}> → </span>
                  <span>{formatVal(newV)}</span>
                </div>
              ))}
              {Object.keys(row.diff).length > 6 && (
                <div className="text-[10px]" style={{ color: "var(--text-tertiary)" }}>
                  +{Object.keys(row.diff).length - 6} more
                </div>
              )}
            </div>
          )}
        </div>
        {canDelete && (
          <button onClick={() => onDelete(row.id)} className="p-1 rounded-full hover:bg-[var(--bg-subtle)]" data-testid={`comment-del-${row.id}`}>
            <Trash size={12} style={{ color: "var(--accent)" }} />
          </button>
        )}
      </div>
    </div>
  );
}

function formatVal(v) {
  if (v == null || v === "") return "—";
  if (Array.isArray(v)) return `[${v.length}]`;
  if (typeof v === "object") return JSON.stringify(v).slice(0, 40);
  const s = String(v);
  return s.length > 40 ? s.slice(0, 37) + "…" : s;
}

/* ---------------------------------------------------------------------------
   <ExportColumnsModal />
   Lets user pick columns + see active filters before exporting Excel.
   Props:
     entityKey ('installed_base' | 'opportunities'),
     filters (object — currently active filters, used in querystring + banner),
     onClose
   --------------------------------------------------------------------------- */
export function ExportColumnsModal({ entityKey, filters = {}, onClose }) {
  const { t } = useI18n();
  const [catalog, setCatalog] = useState(null);
  const [selected, setSelected] = useState(null); // Set<string>

  useEffect(() => {
    api.get("/export/columns").then((r) => {
      const list = r.data[entityKey] || [];
      setCatalog(list);
      setSelected(new Set(list.map((c) => c.key)));
    });
  }, [entityKey]);

  const toggle = (k) => {
    const next = new Set(selected);
    next.has(k) ? next.delete(k) : next.add(k);
    setSelected(next);
  };
  const all = () => setSelected(new Set((catalog || []).map((c) => c.key)));
  const none = () => setSelected(new Set());

  const run = () => {
    if (!selected || selected.size === 0) return;
    const cols = (catalog || []).filter((c) => selected.has(c.key)).map((c) => c.key);
    const qs = new URLSearchParams();
    qs.set("columns", cols.join(","));
    for (const [k, v] of Object.entries(filters)) if (v) qs.set(k, v);
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/export/${entityKey}.xlsx?${qs}`;
    window.open(url, "_blank");
    onClose && onClose();
  };

  const activeFilters = Object.entries(filters).filter(([, v]) => v);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         style={{ background: "rgba(0,0,0,0.4)", backdropFilter: "blur(8px)" }}
         onClick={onClose} data-testid="export-modal">
      <div className="tile p-5 md:p-7 w-full max-w-2xl fade-in" onClick={(e) => e.stopPropagation()}
           style={{ boxShadow: "var(--shadow-lg)", maxHeight: "85vh", display: "flex", flexDirection: "column" }}>
        <div className="flex items-start justify-between mb-1">
          <h3 className="text-[20px] font-semibold tracking-tight">{t("export.title")}</h3>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-[var(--bg-subtle)]"><X size={16} /></button>
        </div>
        <p className="text-[13px] mb-4" style={{ color: "var(--text-secondary)" }}>{t("export.subtitle")}</p>

        {activeFilters.length > 0 && (
          <div className="p-3 rounded-[10px] mb-4" style={{ background: "var(--bg-subtle)" }}>
            <div className="label-tech mb-2">{t("export.activeFilters")}</div>
            <div className="flex flex-wrap gap-1.5">
              {activeFilters.map(([k, v]) => (
                <span key={k} className="pill" data-testid={`export-filter-${k}`}>{k} = {v}</span>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mb-2">
          <div className="label-tech">{t("export.columns")} ({selected?.size || 0}/{catalog?.length || 0})</div>
          <div className="flex gap-2 text-[12px] font-mono">
            <button onClick={all} className="hover:text-[var(--accent)] transition-colors" data-testid="export-all">{t("export.all")}</button>
            <span style={{ color: "var(--border-strong)" }}>|</span>
            <button onClick={none} className="hover:text-[var(--accent)] transition-colors" data-testid="export-none">{t("export.none")}</button>
          </div>
        </div>

        <div className="overflow-y-auto pr-1 grid grid-cols-2 gap-1.5" style={{ flex: 1, maxHeight: "45vh" }}>
          {!catalog ? <div className="col-span-2 text-[12px] font-mono" style={{ color: "var(--text-tertiary)" }}>{t("common.loading")}</div> :
            catalog.map((c) => (
              <label key={c.key} className="flex items-center gap-2 px-3 py-2 rounded-[8px] cursor-pointer text-[13px] hover:bg-[var(--bg-subtle)] transition-colors"
                     data-testid={`export-col-${c.key}`}>
                <input
                  type="checkbox"
                  checked={selected ? selected.has(c.key) : false}
                  onChange={() => toggle(c.key)}
                  className="accent-[var(--accent)]"
                />
                <span>{c.label}</span>
              </label>
            ))}
        </div>

        <div className="flex justify-end gap-2 mt-5 pt-4" style={{ borderTop: "1px solid var(--border)" }}>
          <button onClick={onClose} className="btn-ghost" data-testid="export-cancel">{t("common.cancel")}</button>
          <button onClick={run} disabled={!selected || selected.size === 0} className="btn-accent" data-testid="export-download">
            {t("export.download")} (.xlsx)
          </button>
        </div>
      </div>
    </div>
  );
}
